import { MenuItem, TextField, FormControlLabel, Checkbox, FormHelperText } from "@mui/material";
import { useT } from "../../i18n";

/**
 * C2b — רינדור השדות הנוספים שהלקוח הגדיר בממשק הניהול.
 *
 * ★ הפרונט לא מכיר אף שדה מראש: הרשימה מגיעה מ-`user/site-config`
 *   (‏`visitor_fields`) והרכיב מרנדר לפי `type`. לקוח שלא הגדיר דבר ⇒
 *   ‏`defs` ריק ⇒ הרכיב לא מרנדר כלום והטופס נשאר בדיוק כפי שהיה.
 *
 * ⚠️ הערכים נשמרים תמיד כמחרוזות (‏checkbox = "1"/"0"), כי זה מה שהשרת
 *   מצפה לקבל ולאחסן ב-JSON. המרה מוקדמת ל-boolean/number הייתה יוצרת
 *   פער בין מה שנשלח למה שנשמר.
 *
 * @param {Array}  defs     הגדרות השדות מהשרת
 * @param {Object} values   key => value נוכחיים
 * @param {Object} errors   key => טקסט-שגיאה (מהאב, אחרי ניסיון "שלב הבא")
 * @param {Function} onChange (key, value)
 */
/* ★★ במכשיר-מגע הבורר מרונדר כ-`<select>` **נייטיב**.
   ⚠️ הבאג שזה סוגר (דיווח-משתמש 22/07/2026): במובייל, אחרי מילוי ת"ז → שם
      פרטי → שם משפחה, לחיצה על "הבא" במקלדת **לא עברה** לשדה הנוסף והמקלדת
      נסגרה. הסיבה: ‏MUI מרנדר בורר כ-`div[role=combobox]`, וכפתור "הבא"
      במקלדות המובייל מנווט בין **שדות-קלט** (input/textarea/select) בלבד —
      הוא אינו יכול לנחות על div, ולכן פשוט לא היה לאן להמשיך.
   ★ בונוס: הבורר הנייטיב פותח את בורר-מערכת-ההפעלה, שמטפל ב-RTL ובמסכים
     קטנים טוב יותר מכל תפריט שנצייר בעצמנו.
   ★ בדסקטופ נשאר תפריט-MUI: שם אין מקלדת-מגע, והמראה עקבי עם שאר הטופס.
   ⚠️ נקבע **פעם אחת** בטעינת המודול ולא בכל רינדור — שינוי-הרכיב באמצע
      הקלדה היה מאבד פוקוס. */
const IS_TOUCH = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia('(pointer: coarse)').matches;

function ExtraFields({ defs, values, errors, onChange }) {
  // ⚠️ ה-hook קודם ל-return המוקדם — אחרת סדר-ההוקים משתנה בין רינדורים.
  const t = useT();
  if (!Array.isArray(defs) || defs.length === 0) return null;

  return (
    <>
      {defs.map((f) => {
        const val = values?.[f.key] ?? "";
        const err = errors?.[f.key] || "";

        if (f.type === "checkbox") {
          return (
            <div key={f.key} className="extra-field extra-field--check">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={val === "1"}
                    onChange={(e) => onChange(f.key, e.target.checked ? "1" : "0")}
                  />
                }
                label={f.required ? `${f.label} *` : f.label}
              />
              {(err || f.hint) && (
                <FormHelperText error={Boolean(err)}>{err || f.hint}</FormHelperText>
              )}
            </div>
          );
        }

        // בחירה-מרשימה: TextField עם select כדי שהמראה יהיה זהה לשאר השדות
        // בטופס (אותו outline, אותה תווית צפה) ולא Select חשוף.
        if (f.type === "select") {
          return (
            <TextField
              key={f.key}
              select
              required={f.required}
              value={val}
              label={f.label}
              error={Boolean(err)}
              helperText={err || f.hint || ""}
              onChange={(e) => onChange(f.key, e.target.value)}
              /* ⚠️ בבורר נייטיב התווית **חייבת** להיות מוצמדת-למעלה: הדפדפן
                 מצייר את תוכן ה-option בתוך השדה, והתווית הצפה הייתה יושבת
                 עליו. אותה סיבה בדיוק כמו בשדה מסוג `date` שלמטה. */
              InputLabelProps={IS_TOUCH ? { shrink: true } : undefined}
              /* ★★ יישור מפורש לימין. ‏`.App` מגדיר `text-align:center` גלובלי
                 (שארית תבנית create-react-app) שנרש בירושה אל תיבת-הבחירה
                 של MUI, ולכן הערך **שנבחר** הוצג ממורכז בעוד שאר השדות
                 בטופס מיושרים לימין.
                 ★ התיקון מקומי ולא בכלל הגלובלי: שינוי `.App` היה משפיע על
                   כל האתר, כולל מסכים שמסתמכים על המרכוז הזה. */
              /* ⚠️⚠️⚠️ **`stylis-plugin-rtl` מהפך גם את `direction` עצמו.**
                 אומת בהרצה על התוסף: `direction:rtl` יוצא `direction:ltr`.
                 לכן הגרסה הקודמת, שכתבה `direction:'rtl'` *וגם* `textAlign:'start'`,
                 ירתה בעצמה ברגל: ה-`direction` התהפך ל-ltr, ו-`start` — שהוא
                 תכונה לוגית הנגזרת מה-direction — נפתר כ**שמאל**. המספרים
                 הופיעו בצד שמאל (דיווח-משתמש 22/07/2026).
                 ⇒ **לא מצהירים `direction` בכלל.** הדף כבר RTL בירושה,
                   ו-`start` נפתר נכון מעצמו. הצהרה מפורשת היא בדיוק מה
                   שהתוסף הופך — ולכן היא מזיקה, לא מיותרת.
                 ★ הלקח הרחב: התוסף אינו "מהפך left/right" אלא **מהפך כיווניות**.
                   כל ניסיון לכפות כיוון ידנית ייהפך. */
              sx={{ '& .MuiSelect-select': { textAlign: 'start' },
                    '& select': { textAlign: 'start' } }}
              SelectProps={{
                native: IS_TOUCH,
                MenuProps: IS_TOUCH ? undefined : {
                  PaperProps: {
                    sx: {
                      '& .MuiMenuItem-root': {
                        textAlign: 'start', justifyContent: 'flex-start',
                      },
                    },
                  },
                },
              }}
            >
              {/* ⚠️ בורר נייטיב מקבל `<option>` ובורר-MUI מקבל `<MenuItem>` —
                  ערבוב ביניהם מרנדר תפריט ריק בשקט.
                  ★ אפשרות ריקה רק כששדה אינו חובה — אחרת היא "דרך לעקוף".
                    בנייטיב היא **חייבת** להתקיים גם בשדה-חובה שטרם נבחר,
                    אחרת הדפדפן בוחר אוטומטית את הראשונה והמשתמש "מילא"
                    ערך שלא בחר. ⇒ ריקה מוסתרת + disabled. */}
              {IS_TOUCH ? [
                (f.required
                  ? <option key="__ph" value="" disabled hidden>{t('extraFields.selectPlaceholder')}</option>
                  : <option key="__ph" value="">—</option>),
                ...(f.options || []).map((o) => <option key={o} value={o}>{o}</option>),
              ] : [
                ...(!f.required ? [<MenuItem key="__ph" value="">—</MenuItem>] : []),
                ...(f.options || []).map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>),
              ]}
            </TextField>
          );
        }

        const type = f.type === "number" ? "number"
          : f.type === "date" ? "date"
          : f.type === "email" ? "email" : "text";

        return (
          <TextField
            key={f.key}
            required={f.required}
            value={val}
            label={f.label}
            type={type}
            error={Boolean(err)}
            helperText={err || f.hint || ""}
            onChange={(e) => onChange(f.key, e.target.value)}
            // תאריך: התווית חייבת להישאר מוצמדת-למעלה, אחרת היא מתנגשת
            // בפורמט dd/mm/yyyy שהדפדפן מצייר בשדה ריק.
            InputLabelProps={type === "date" ? { shrink: true } : undefined}
            inputProps={
              type === "number"
                ? { min: f.min ?? undefined, max: f.max ?? undefined, inputMode: "numeric" }
                : undefined
            }
          />
        );
      })}
    </>
  );
}

export default ExtraFields;
